import {
  Progress,
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import { useEffect } from 'react'
import useGetSurveyQuotas from '../../../../hooks/useGetSurveyQuotas'

const useStyles = makeStyles()({
  // Stack based on the card's available width, not the whole viewport.
  breakdown: { containerType: 'inline-size' },
  groupLabel: {
    textTransform: 'uppercase'
  },
  group: { marginTop: 16 },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 1fr) 64px 260px',
    alignItems: 'center',
    gap: 16,
    padding: '14px 0',
    borderBottom: '1px solid #E5EAF5',
    '@container (max-width: 600px)': {
      gridTemplateColumns: '64px minmax(0, 1fr)'
    }
  },
  label: {
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    '@container (max-width: 600px)': { gridColumn: '1 / -1' }
  },
  headings: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 1fr) 64px 260px',
    alignItems: 'center',
    gap: 16,
    padding: '8px 0',
    color: '#6B7280',
    '@container (max-width: 600px)': { display: 'none' }
  },
  share: { color: '#42526E', textAlign: 'center' },
  progress: { minWidth: 0 },
  state: { color: '#6B7280', padding: '8px 0' }
})

/**
 * Per-market quota fill, grouped by quota (Age, Gender, …). Each option row
 * shows collected / wanted with fulfilment progress rounded down (the bar is
 * capped at 100%), plus the cell's share of the total sample in a separate
 * Quota % column. Quotas are loaded lazily — only when a market's breakdown
 * is expanded — via the REST quotas endpoint.
 */
export const MarketQuotaBreakdown = ({ surveyId }: { surveyId: string }) => {
  const { classes } = useStyles()
  const { getSurveyQuotas, quotaResults, loading, error } = useGetSurveyQuotas({
    surveyId
  })

  useEffect(() => {
    getSurveyQuotas(surveyId)
  }, [surveyId, getSurveyQuotas])

  if (loading && !quotaResults) {
    return (
      <Text size={TextSize.ms}>
        <span className={classes.state}>Loading quota breakdown…</span>
      </Text>
    )
  }
  if (error) {
    return (
      <Text size={TextSize.ms}>
        <span className={classes.state}>
          Couldn’t load the quota breakdown.
        </span>
      </Text>
    )
  }
  // The quota-results endpoints track fill against a placed order, so they are
  // empty both for quota-less markets and before fieldwork starts — don't
  // claim "no quotas configured" (they may well be, on the Audience step).
  if (!quotaResults || quotaResults.length === 0) {
    return (
      <Text size={TextSize.ms}>
        <span className={classes.state}>
          No quota tracking data for this market yet.
        </span>
      </Text>
    )
  }

  return (
    <div className={classes.breakdown}>
      {quotaResults.map((quota) => (
        <div key={quota.id} className={classes.group}>
          <Text
            size={TextSize.s}
            weight={TextWeight.SemiBold}
            className={classes.groupLabel}
          >
            {quota.text}
          </Text>
          <div className={classes.headings}>
            <span />
            <span className={classes.share}>
              <Text size={TextSize.s} weight={TextWeight.SemiBold}>
                QUOTA %
              </Text>
            </span>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              CURRENT PROGRESS
            </Text>
          </div>
          {quota.options.map((option) => {
            // API progress is capped at 1, so derive uncapped progress from counts.
            const { samplesCollected, samplesWanted } = option.progress
            const progressPercentage =
              samplesWanted > 0
                ? Math.floor((samplesCollected / samplesWanted) * 100)
                : 0
            const share = Math.round(option.quotaPercent * 100)
            return (
              <div key={option.id} className={classes.row}>
                <div className={classes.label}>
                  <Text size={TextSize.ms}>{option.text}</Text>
                </div>
                <div className={classes.share}>
                  <Text size={TextSize.ms}>{share}%</Text>
                </div>
                <div className={classes.progress}>
                  <Progress
                    value={Math.min(progressPercentage, 100)}
                    progressPercentage={progressPercentage}
                    wanted={samplesWanted}
                    collected={samplesCollected}
                  />
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
